import { Location } from "@angular/common";
import { Component, OnInit, OnDestroy } from "@angular/core";
import { ActivatedRoute, NavigationStart, Router } from "@angular/router";
import { AzureSearchService } from "@rubbl-azure-search";
import { CartItem, CartService, Charge, MachineCardAddToCartComponent } from "@rubbl-cart";
import {
  EnvironmentConfig,
  WindowService,
  LogService,
  ModalService,
  MarketTrackingEvents,
  SeoService,
  AddressPipe,
  SeoRouteData,
  ShareService,
} from "@rubbl-core";
import { InventoryItem, Attachment, InventoryService } from "@rubbl-inventory";
import { HeaderService } from "@rubbl-layout";

import { deepClone } from "fast-json-patch";
import { ToastrService } from "ngx-toastr";
import { Observable, Subscription } from "rxjs";

declare const environment: EnvironmentConfig;
@Component({
  selector: "app-machine-details-layout",
  templateUrl: "./machine-details-layout.component.html",
  styleUrls: ["./machine-details-layout.component.scss"],
})
export class MachineDetailsLayoutComponent implements OnInit, OnDestroy {
  machine: InventoryItem;
  cartItem: CartItem;
  isEstimate: boolean;
  filteredAttachments: string[] = [];
  fixedAttachments: Attachment[] = [];
  nonFixedAttachments: Attachment[] = [];

  windowSubscription: Subscription;
  cartSubscription: Subscription;
  windowSize: string;
  routerSub: Subscription;
  hasStockPhotos: boolean = false;
  machineViewCount$: Observable<number>;
  hasHistory: boolean;

  constructor(
    private headerService: HeaderService,
    private route: ActivatedRoute,
    private windowService: WindowService,
    private logService: LogService,
    private router: Router,
    private cartService: CartService,
    private azureSearchService: AzureSearchService,
    private modalService: ModalService,
    private toastrService: ToastrService,
    private seoService: SeoService,
    private addressPipe: AddressPipe,
    private inventoryService: InventoryService,
    private shareService: ShareService,
    private location: Location,
  ) {}

  ngOnInit() {
    this.filteredAttachments = Object.entries(history.state)
      .filter(([key, value]) => key != "navigationId")
      .map(([key, value]) => {
        return value;
      }) as string[]; // stupidest fucking idea ever.  because yeah lets filter attachments and have them automatically selected

    this.headerService.showDefaultHeaderItems();

    this.route.data.subscribe((data: { machine: InventoryItem }) => {
      this.inventoryService.counterUp(data.machine).subscribe();

      this.setSeoData(data.machine);
      this.setSeoData(data.machine);

      this.machine = data.machine;
      this.fixedAttachments = data.machine.relatedAttachments.filter(d => d.isFixed);
      this.nonFixedAttachments = data.machine.relatedAttachments.filter(d => !d.isFixed);
      this.logService.trackEvent(MarketTrackingEvents.MachineDetails.Visit, { inventoryId: this.machine.id });

      const firstThumbNail = this.machine?.thumbnails[0];
      if (firstThumbNail && firstThumbNail.includes("pubweb/catalog")) {
        this.hasStockPhotos = true;
      }
    });

    // i really hate this solution but since the stupid photo swiper needs to be reinitilized each time we update the photos i need to do this
    this.routerSub = this.router.events.subscribe((e: any) => {
      if (e instanceof NavigationStart) {
        this.machine = null;
      }
    });

    this.cartSubscription = this.cartService.cartBSubject.subscribe(cart => {
      this.cartItem = this.cartService.getItemByInventoryItemId(this.machine.id);
      if (!this.cartItem) {
        this.getEstimate();
      } else {
        this.isEstimate = false;
        window["prerenderReady"] = true;
      }
    });

    // subscribe to config updates
    this.windowSubscription = this.windowService.windowSizeBSubject.subscribe(windowSize => (this.windowSize = windowSize));

    this.hasHistory = this.router.navigated;
  }

  private setSeoData(machine: InventoryItem) {
    const image = machine.images.length > 0 ? environment.baseImagePath + machine.images[0] : null;
    let keywords = ["construction", "equipment", machine.primaryType, machine.make, machine.model, machine.location.name];

    // Set SEO data for Rent Only
    let title = `${machine.year} ${machine.make} ${machine.model} ${machine.primaryType} in ${this.addressPipe.transform(
      machine.location.address,
      "cityState",
    )}`;
    let description = `Rent this ${machine.year} ${machine.make} ${machine.model} ${machine.primaryType} located in ${this.addressPipe.transform(
      machine.location.address,
      "cityState",
    )}. Find similar construction rentals online near ${this.addressPipe.transform(
      machine.location.address,
      "cityState",
    )}. Explore prices and our real-time inventory.`;

    if (machine.rentalRate > 0) {
      keywords.push("rental", "rent", "lease", "monthly", "weekly");
    }

    // Set SEO data for Rent, Rent to Own, and Buy It Now
    if (machine.rentalRate > 0 && machine.rpoEnabled && machine.buyItNowEnabled) {
      title = `Rent, Rent to Own, or Buy this ${machine.year} ${machine.make} ${machine.model} ${machine.primaryType} located in ${this.addressPipe.transform(
        machine.location.address,
        "cityState",
      )}`;
      description = `Rent, Rent to Own, or Buy this ${machine.year} ${machine.make} ${machine.model} ${
        machine.primaryType
      } located in ${this.addressPipe.transform(machine.location.address, "cityState")}. Find construction rentals in ${this.addressPipe.transform(
        machine.location.address,
        "cityState",
      )} and surrounding areas online. Explore prices and our real-time inventory.`;
      keywords.push("rpo", "rto", "rent to own", "rent to own", "rental purchase option", "for sale", "sale", "purchase", "buy", "buy it now");

      // Set SEO data for Rent and Rent To Own
    } else if (machine.rentalRate > 0 && machine.rpoEnabled) {
      title = `Rent or Rent to Own this ${machine.year} ${machine.make} ${machine.model} ${machine.primaryType} located in ${this.addressPipe.transform(
        machine.location.address,
        "cityState",
      )}`;
      description = `Rent or Rent to Own this ${machine.year} ${machine.make} ${machine.model} ${machine.primaryType} located in ${this.addressPipe.transform(
        machine.location.address,
        "cityState",
      )}. Find construction rentals in ${this.addressPipe.transform(
        machine.location.address,
        "cityState",
      )} and surrounding areas online. Explore prices and our real-time inventory.`;
      keywords.push("rpo", "rto", "rent to own", "rental purchase option");

      // Set SEO data for Buy It Now Only
    } else if (machine.buyItNowOnly) {
      title = `Buy this ${machine.year} ${machine.make} ${machine.model} ${machine.primaryType} located in ${this.addressPipe.transform(
        machine.location.address,
        "cityState",
      )}`;
      description = `Buy this ${machine.year} ${machine.make} ${machine.model} ${machine.primaryType} located in ${this.addressPipe.transform(
        machine.location.address,
        "cityState",
      )}. Find construction rentals in ${this.addressPipe.transform(
        machine.location.address,
        "cityState",
      )} and surrounding areas online. Explore prices and our real-time inventory.`;
      keywords = ["construction", "equipment", "purchase", "buy", "buy it now", machine.primaryType, machine.make, machine.model, machine.location.name];

      // Set SEO for Rent and Buy It Now
    } else if (machine.rentalRate > 0 && machine.buyItNowEnabled) {
      title = `Rent or Buy this ${machine.year} ${machine.make} ${machine.model} ${machine.primaryType} located in ${this.addressPipe.transform(
        machine.location.address,
        "cityState",
      )}`;
      description = `Rent or Buy this ${machine.year} ${machine.make} ${machine.model} ${machine.primaryType} located in ${this.addressPipe.transform(
        machine.location.address,
        "cityState",
      )}. Find construction rentals in ${this.addressPipe.transform(
        machine.location.address,
        "cityState",
      )} and surrounding areas online. Explore prices and our real-time inventory.`;
      keywords.push("purchase", "buy", "buy it now");
    }

    const seoData = {
      title,
      description,
      keywords: keywords.join(","),
      image,
    };
    this.seoService.setSeo(seoData as SeoRouteData);
  }

  getEstimate() {
    // need to iterate over the filtered attachments and select attachments
    let installFirst = true;
    for (const filteredAttachment of this.filteredAttachments) {
      const attachment = this.machine.relatedAttachments.find(d => d.size == filteredAttachment);
      if (attachment) {
        attachment.accountId = this.machine.accountId;
        attachment.selected = true;
        attachment.childItemId = attachment.inventoryItemId; // cart item uses child item id
        attachment.price = attachment.cost; // cart item uses price
        // lets install the first one
        if (installFirst) {
          attachment.install = true;
          installFirst = false;

          // if the attachment is standard set the cost to 0
          if (attachment.standard) {
            attachment.price = attachment.cost = 0;
          }
        }
      }
    }

    let cartItemEstimate: CartItem = new CartItem(
      new Date(),
      7,
      this.machine,
      this.machine.relatedAttachments.filter(d => d.selected && d.standard).concat(this.machine.relatedAttachments.filter(d => d.selected && !d.standard)),
      false,
    );
    cartItemEstimate.charges = [new Charge(this.machine.rentalRate, "Rent", "Monthly")];

    this.cartService.getCartItemEstimate(cartItemEstimate).subscribe((cartItem: CartItem) => {
      if (!this.cartItem) {
        this.isEstimate = true;
        this.cartItem = cartItem;
      }

      setTimeout(() => {
        window["prerenderReady"] = true;
      }, 1000);
    });
  }

  ngOnDestroy() {
    this.cartSubscription?.unsubscribe();
    this.windowSubscription?.unsubscribe();
    this.routerSub?.unsubscribe();
  }

  goBack() {
    this.location.back();
  }

  addToCart() {
    this.logService.trackEvent(MarketTrackingEvents.Cart.InitialAddToCartClick, { inventoryId: this.machine.id });
    this.logService.sendGoogleEcommerceView("add_to_cart_start_click", [this.machine], "machine-details", "Machine Details");

    const modalRef = this.modalService.standardModal(MachineCardAddToCartComponent, true);
    modalRef.componentInstance.machine = deepClone(this.machine);
    modalRef.componentInstance.machineInCart = false;
    modalRef.result.then(
      result => {
        this.windowService.scrollToElement("#add-to-cart-details");
      },
      stepName => {
        // if we checked out and hit continue shopping we need to scroll up to the add to cart details
        if (stepName === "continueCheckoutStep") {
          setTimeout(() => {
            this.windowService.scrollToElement("#add-to-cart-details");
          }, 300);
        }
      },
    );
  }

  editCartItem(cartItem: CartItem) {
    this.azureSearchService.getOne(cartItem.inventoryItem.id, "machines").subscribe((machine: InventoryItem) => {
      const modalRef = this.modalService.standardModal(MachineCardAddToCartComponent, true);
      modalRef.componentInstance.machine = machine;
      modalRef.componentInstance.machineInCart = true;
      modalRef.componentInstance.inCheckout = true;
      modalRef.componentInstance.cartItem = deepClone(cartItem);
      this.cartItem = null;
      modalRef.result.then(
        result => {
          this.toastrService.success(`Item updated`, null, { positionClass: "toast-top-center" });
        },
        () => {
          this.cartItem = modalRef.componentInstance.cartItem;
        },
      );
    });
  }

  share() {
    let title = `Check this out on Rubbl!`;
    let description = `A ${this.machine.year} ${this.machine.make} ${this.machine.model} is available`;

    if (this.machine.rentalRate > 0 && this.machine.rpoEnabled && this.machine.buyItNowEnabled) {
      description += ` for rent, rent-to-own, or purchase`;
    } else if (this.machine.rentalRate > 0 && this.machine.rpoEnabled) {
      description += ` for rent or rent-to-own`;
    } else if (this.machine.buyItNowOnly) {
      description += ` for sale`;
    } else if (this.machine.rentalRate > 0 && this.machine.buyItNowEnabled) {
      description += ` for rent or purchase`;
    } else {
      description += ` for rental or sale`;
    }

    description += ` near ${this.machine.location.name}.`;

    this.shareService.share(title, description, this.inventoryService.machineDetailsUrl(this.machine), { target: "searchResults-machineShare" });
  }
}
